"use client";

import { ApiProvider, AuthToken, useApiReady } from "./api/api-provider.js";
import { OrganizationInfo, useOrganizations } from "./api/endpoint.js";
import * as React from "react";
import { ErrorBoundary } from "./lib/error-boundary.js";
import {
  OrganizationSwitcherError,
  OrganizationSwitcherLoading,
  OrganizationSwitcher as OrganizationSwitcherPresentational,
} from "./lib/organization-switcher.js";
import type {
  OrganizationSwitcherErrorProps,
  OrganizationSwitcherLoadingProps,
  OrganizationSwitcherPassthroughProps,
} from "./lib/organization-switcher.js";
import { useWorkOsApiUrl } from "./lib/widgets-context.js";
import { keepPreviousData } from "@tanstack/react-query";

interface OrganizationSwitcherProps extends OrganizationSwitcherPassthroughProps {
  authToken: AuthToken;
}

const OrganizationSwitcher: React.FC<OrganizationSwitcherProps> = ({
  authToken,
  organizationLabel,
  switchToOrganization,
  truncateBehavior,
  variant,
  ...domProps
}) => {
  const baseUrl = useWorkOsApiUrl();

  return (
    <ErrorBoundary
      fallbackRender={({ error }) => (
        <OrganizationSwitcherError
          error={error}
          variant={variant}
          {...domProps}
        />
      )}
    >
      <ApiProvider
        widgetType="organization-switcher"
        authToken={authToken}
        baseUrl={baseUrl}
      >
        <OrganizationSwitcherImpl
          switchToOrganization={switchToOrganization}
          organizationLabel={organizationLabel}
          truncateBehavior={truncateBehavior}
          variant={variant}
          {...domProps}
        />
      </ApiProvider>
    </ErrorBoundary>
  );
};

// Constant reference to avoid unnecessary prop changes
const emptyOrganizations: OrganizationInfo[] = [];

const OrganizationSwitcherImpl = ({
  variant,
  switchToOrganization,
  organizationLabel,
  truncateBehavior,
  children,
  ...domProps
}: OrganizationSwitcherPassthroughProps) => {
  const isApiReady = useApiReady();
  const organizationsQuery = useOrganizations({
    query: {
      placeholderData: keepPreviousData,
    },
  });

  const organizations = organizationsQuery.data?.data ?? emptyOrganizations;

  if (organizationsQuery.isLoading || !isApiReady) {
    return <OrganizationSwitcherLoading variant={variant} {...domProps} />;
  }

  if (organizationsQuery.isError) {
    return (
      <OrganizationSwitcherError
        error={organizationsQuery.error}
        variant={variant}
        {...domProps}
      />
    );
  }

  return (
    <ErrorBoundary
      fallbackRender={({ error }) => (
        <OrganizationSwitcherError
          error={error}
          variant={variant}
          {...domProps}
        />
      )}
    >
      <OrganizationSwitcherPresentational
        organizations={organizations}
        switchToOrganization={switchToOrganization}
        variant={variant}
        organizationLabel={organizationLabel}
        truncateBehavior={truncateBehavior}
        {...domProps}
      >
        {children}
      </OrganizationSwitcherPresentational>
    </ErrorBoundary>
  );
};

export type {
  OrganizationSwitcherProps,
  OrganizationSwitcherLoadingProps,
  OrganizationSwitcherErrorProps,
};
export {
  OrganizationSwitcher,
  OrganizationSwitcherLoading,
  OrganizationSwitcherError,
};
